import React, { useState, useEffect, useMemo, useRef } from "react";
import { Eye, EyeOff, MoreHorizontal, Users, FileText, Download, X, Copy, CalendarClock } from "lucide-react";
import { useSelector } from "react-redux";
import axios from "axios";
import { jsPDF } from "jspdf";
import CandidateDetailTabs from "./CandidateDetailTabs";
import useScheduleInterview from "./projects/useScheduleInterview";
import {
    INVALID_EMAIL_MESSAGE,
    INVALID_PHONE_MESSAGE,
    isValidEmail,
    isValidPhone,
    PhoneNumberInput,
    toE164,
} from "./ContactFields";

const STATUS_OPTIONS = [
    { value: "not_contacted",  label: "Not Contacted",  color: "#93c5fd" },
    { value: "email_sent",     label: "Email Sent",     color: "#0d9488" },
    { value: "interested",     label: "Interested",     color: "#a78bfa" },
    { value: "rejected",       label: "Rejected",       color: "#ef4444" },
    { value: "not_interested", label: "Not Interested", color: "#9ca3af" },
];

const candidateInitials = (name) =>
    (name || "?").split(" ").filter(Boolean).slice(0, 2).map(n => n[0]).join("").toUpperCase() || "?";

/**
 * Candidate profile avatar. Shows the profile picture and falls back to initials
 * when there's no URL or the image fails to load (LinkedIn/Crustdata CDNs often
 * block hotlinking). `referrerPolicy="no-referrer"` helps those images load.
 */
export const CandidateAvatar = ({ candidate, size = 40, className = "" }) => {
    const [errored, setErrored] = useState(false);
    const url = candidate?.profile_picture_url;
    const px = `${size}px`;

    if (url && !errored) {
        return (
            <img
                src={url}
                alt={candidate?.full_name || ""}
                onError={() => setErrored(true)}
                referrerPolicy="no-referrer"
                className={`rounded-full object-cover shrink-0 bg-gray-100 dark:bg-white/10 ${className}`}
                style={{ width: px, height: px }}
            />
        );
    }
    return (
        <div
            className={`rounded-full flex items-center justify-center text-white font-semibold shrink-0 ${className}`}
            style={{ width: px, height: px, fontSize: Math.round(size * 0.36), background: "linear-gradient(135deg, #a78bfa, #60a5fa)" }}
        >
            {candidateInitials(candidate?.full_name)}
        </div>
    );
};

export const CandidateCard = ({ candidate, onClick, shortlisted, onShortlist, renderAction, hidden, onHide, selected, onSelect, isSelected = false }) => {
    const pastJob = candidate.past_jobs?.[0];
    const education = candidate.education?.[0];

    const handleShortlist = (e) => {
        e.stopPropagation();
        onShortlist(candidate);
    };

    const handleHide = (e) => {
        e.stopPropagation();
        onHide?.(candidate);
    };

    return (
        <div
            onClick={onClick}
            className={`w-full border-b border-gray-100 px-6 py-5 transition-all ${hidden ? "bg-gray-100" : "bg-white hover:bg-gray-50"}`}>
            <div className="flex items-start justify-between gap-4">

                <input
                    type="checkbox"
                    checked={selected ?? false}
                    onChange={(e) => { e.stopPropagation(); onSelect?.(candidate, e.target.checked); }}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-1 shrink-0 accent-purple-600 cursor-pointer"
                />

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`text-base font-semibold ${hidden ? "text-gray-400" : "text-gray-900"}`}>
                            {candidate.full_name}
                        </h3>
                        {!hidden && candidate.linkedin_url && (
                            <a
                                href={candidate.linkedin_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 dark:text-blue-300 hover:text-blue-700"
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                </svg>
                            </a>
                        )}
                    </div>

                    {candidate.current_title && (
                        <div className="flex items-center gap-2 mt-1.5">
                            {candidate.current_company && (
                                <div className="w-4 h-4 rounded bg-gray-200 shrink-0" />
                            )}
                            <p className={`text-sm ${hidden ? "text-gray-400" : "text-gray-600"}`}>
                                {candidate.current_title}
                                {candidate.current_company && (
                                    <span> at {candidate.current_company}</span>
                                )}
                                {candidate.location && (
                                    <span className="text-gray-400 dark:text-gray-500"> · {candidate.location}</span>
                                )}
                            </p>
                        </div>
                    )}

                    {!hidden && pastJob && (
                        <div className="flex items-center gap-2 mt-1">
                            <div className="w-4 h-4 rounded bg-gray-100 dark:bg-white/10 shrink-0" />
                            <p className="text-sm text-gray-400 dark:text-gray-500">
                                {pastJob.title}
                                {pastJob.name && <span> at {pastJob.name}</span>}
                            </p>
                        </div>
                    )}

                    {!hidden && education && (
                        <div className="flex items-center gap-2 mt-1">
                            <div className="w-4 h-4 rounded bg-gray-100 dark:bg-white/10 shrink-0" />
                            <p className="text-sm text-gray-400 dark:text-gray-500">
                                {education.degree && <span>{education.degree}, </span>}
                                {education.field_of_study && (
                                    <span>{education.field_of_study} </span>
                                )}
                                {education.school && <span>at {education.school}</span>}
                            </p>
                        </div>
                    )}

                    {!hidden && candidate.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                            {candidate.skills.slice(0, 5).map((skill, i) => (
                                <span
                                    key={i}
                                    className="px-2.5 py-0.5 rounded-full bg-purple-50 dark:bg-purple-500/15 text-purple-600 dark:text-purple-300 text-xs font-medium"
                                >
                                    {skill}
                                </span>
                            ))}
                            {candidate.skills.length > 5 && (
                                <span className="px-2.5 py-0.5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-400 dark:text-gray-500 text-xs">
                                    +{candidate.skills.length - 5}
                                </span>
                            )}
                        </div>
                    )}

                    {!hidden && (<div className="flex flex-wrap gap-2 mt-3">
                        {candidate.years_of_experience > 0 && (
                            <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-300 text-xs font-medium">
                                👍 {candidate.years_of_experience} yrs experience
                            </span>
                        )}
                        {candidate.has_business_email && (
                            <span className="px-2.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-500/15 text-blue-600 dark:text-blue-300 text-xs font-medium">
                                ✉ Email available
                            </span>
                        )}
                        {candidate.recently_changed_jobs && (
                            <span className="px-2.5 py-0.5 rounded-full bg-orange-50 text-orange-600 text-xs font-medium">
                                🔄 Recently changed jobs
                            </span>
                        )}
                    </div>)}
                </div>

                <div className="flex items-start gap-2 shrink-0">
                    <button
                        onClick={handleHide}
                        title={hidden ? "Show profile" : "Hide profile"}
                        className="mt-0.5 text-gray-300 dark:text-gray-600 hover:text-gray-500 transition-colors"
                    >
                        {hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    {!hidden && (renderAction ? renderAction(candidate) : (
                        <button
                            onClick={handleShortlist}
                            className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium border transition-all ${
                                shortlisted
                                    ? "text-white border-transparent"
                                    : "bg-white text-gray-600 border-gray-200 hover:border-pink-400 hover:text-pink-500"
                            }`}
                            style={shortlisted ? { background: "linear-gradient(to right, #ec4899, #3b82f6)" } : undefined}
                        >
                            {shortlisted ? "✓ Shortlisted" : "Shortlist"}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const CandidatePanel = ({ candidate: listCandidate, onClose, shortlisted, onShortlist, onEnroll, onContactUpdate, onFindSimilar, jobId = null, onScheduled }) => {
    // Work history and education are NOT in the list payload — carrying them for a whole
    // page cost ~240 ms and roughly two thirds of the response, while the list itself only
    // shows name, contact, current role and skills. They are fetched here instead, for the
    // one candidate actually opened, and merged over the list row so every `candidate.*`
    // read below keeps working untouched (absent until it arrives, so the sections just
    // render empty for a moment rather than breaking).
    const [profile, setProfile] = useState(null);
    const candidateId = listCandidate?.candidate_id;

    useEffect(() => {
        setProfile(null);
        if (!candidateId) return undefined;
        let cancelled = false;
        axios
            .get(`${process.env.REACT_APP_SOURCING_API_URL}/sourcing/candidate/${candidateId}/profile`)
            .then(({ data }) => { if (!cancelled) setProfile(data); })
            .catch(() => {
                // A failed profile read must not blank the panel — the rest of the
                // candidate is already on screen and still usable.
                if (!cancelled) setProfile({ current_jobs: [], past_jobs: [], education: [] });
            });
        return () => { cancelled = true; };
    }, [candidateId]);

    const candidate = useMemo(
        () => (profile ? { ...listCandidate, ...profile } : listCandidate),
        [listCandidate, profile],
    );

    const recruiterName = useSelector(state => state.userSlice?.name) || localStorage.getItem("username") || "";
    // Scheduling moves the candidate to Interview Management, so the panel is showing
    // someone the list behind it no longer holds — close it and let the owner refresh.
    const { startSchedule, scheduleUI, canSchedule } = useScheduleInterview(jobId, (ids) => {
        onScheduled?.(ids);
        onClose?.();
    });
    const [email, setEmail] = useState(null);
    const [emailType, setEmailType] = useState(null);
    const [revealing, setRevealing] = useState(false);
    const [revealError, setRevealError] = useState(null);
    const [shortlisting, setShortlisting] = useState(false);
    const [manualEntry, setManualEntry] = useState(false);
    const [manualInput, setManualInput] = useState("");
    const [savingManual, setSavingManual] = useState(false);
    const [phone, setPhone] = useState(null);
    const [checkingPhone, setCheckingPhone] = useState(false);
    const [phoneManualEntry, setPhoneManualEntry] = useState(false);
    const [phoneInput, setPhoneInput] = useState("");
    const [savingPhone, setSavingPhone] = useState(false);
    const [phoneError, setPhoneError] = useState(null);
    const [activeTab] = useState("overview");
    const [showDetail, setShowDetail] = useState(false);
    // status and enrollment ride along on the row (every list endpoint stamps them), so
    // seed from the candidate rather than starting on a placeholder the fetch corrects a
    // moment later — that correction is what made the panel look like it changed its mind.
    const [pipelineStatus, setPipelineStatus] = useState(candidate?.status || "not_contacted");
    const [otherJobs, setOtherJobs] = useState({ pipeline: [], sequences: [] });
    const [enrollment, setEnrollment] = useState(candidate?.enrollment ?? null);
    const [enrollmentName, setEnrollmentName] = useState(candidate?.enrollment?.sequence_name ?? null);
    // Only unknown when the row didn't carry the field at all — i.e. a frontend running
    // ahead of the backend that stamps it. Then, and only then, we fetch.
    const [enrollmentLoading, setEnrollmentLoading] = useState(candidate?.enrollment === undefined);
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const [summaryOpen, setSummaryOpen] = useState(false);
    const statusDropdownRef = useRef(null);
    const moreRef = useRef(null);
    const hasLoggedRef = useRef(false);

    useEffect(() => {
        if (!candidate) return;
        if (hasLoggedRef.current) return;
        hasLoggedRef.current = true;
        axios.post(
            `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/activity`,
            {
                action: "viewed",
                candidate_id: candidate.candidate_id || null,
                recruiter_id: localStorage.getItem("org_admin_id"),
                organisation_id: localStorage.getItem("current_org_id"),
                metadata: {
                    recruiter_name: recruiterName,
                    candidate_name: candidate.full_name,
                    current_title: candidate.current_title,
                    current_company: candidate.current_company,
                    source: candidate.source,
                    linkedin_url: candidate.linkedin_url,
                }
            }
        ).catch(() => {});
    }, [candidate?.candidate_id]);

    useEffect(() => {
        const handler = (e) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
                setStatusDropdownOpen(false);
            }
            if (moreRef.current && !moreRef.current.contains(e.target)) {
                setMoreOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        if (!candidate?.candidate_id) return;
        const recruiterId = localStorage.getItem("org_admin_id");
        const orgId = localStorage.getItem("current_org_id");

        // The row already carries this job's status — adopt it now so the dropdown paints
        // right on open, and on switching candidates while the panel stays mounted.
        setPipelineStatus(candidate.status || "not_contacted");

        // The read below still runs, but only for its side effect: it seeds a
        // "not_contacted" row for a candidate who has no pipeline event yet, which the row
        // can't tell us (an absent event and an explicit not_contacted look identical
        // there). It agrees with what we just set, so it changes nothing on screen.
        //
        // Status is per-role: this reads the candidate's standing on *this* job, so a
        // brand-new job role starts at Not Contacted no matter what happened elsewhere.
        axios.get(
            `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/pipeline/${candidate.candidate_id}`,
            { params: { recruiter_id: recruiterId, organisation_id: orgId, job_id: jobId } }
        ).then(({ data }) => {
            if (data.status) {
                setPipelineStatus(data.status);
            } else {
                // No prior event — start everyone at "Not Contacted". Only seed a row when
                // we know the job; an unscoped row would belong to no role.
                setPipelineStatus("not_contacted");
                if (jobId) {
                    axios.post(`${process.env.REACT_APP_SOURCING_API_URL}/sourcing/pipeline`, {
                        candidate_id: candidate.candidate_id,
                        recruiter_id: recruiterId,
                        organisation_id: orgId,
                        action: "not_contacted",
                        job_id: jobId,
                    }).catch(() => {});
                }
            }
        }).catch(() => {
            // Keep whatever the row gave us. Resetting here would turn a failed request
            // into a visibly wrong status for someone who has actually been contacted.
        });
    }, [candidate?.candidate_id, jobId]);

    // Where else we've engaged this person. Kept separate from the status above so
    // history informs the recruiter without masquerading as this role's pipeline.
    useEffect(() => {
        if (!candidate?.candidate_id) return;
        axios.get(
            `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/candidate/${candidate.candidate_id}/history`,
            {
                params: {
                    organisation_id: localStorage.getItem("current_org_id"),
                    exclude_job_id: jobId,
                },
            }
        ).then(({ data }) => setOtherJobs({
            // Only rows we can name. A pipeline row with no resolvable job title —
            // pre-scoping (job_id NULL) or a job since deleted — gives the recruiter a
            // status with nothing to attach it to, so drop it rather than show a
            // placeholder. Filtering here (not at render) keeps the section's
            // show/hide check honest: no named rows and no sequences, no section.
            pipeline: (data.pipeline || []).filter(r => r.job_role),
            sequences: data.sequences || [],
        })).catch(() => setOtherJobs({ pipeline: [], sequences: [] }));
    }, [candidate?.candidate_id, jobId]);

    // Whether this person is already in an outreach decides what the primary action says,
    // so it has to be known before the panel paints — the list endpoints stamp it on the
    // row for exactly that reason, and normally this costs no request at all.
    //
    // The fetch below is only the fallback for a row that predates that field, so the
    // panel still works if the frontend ships ahead of the backend. Once deployed it
    // never runs, and it can be deleted then.
    useEffect(() => {
        if (!candidate?.candidate_id) { setEnrollmentLoading(false); return; }

        if (candidate.enrollment !== undefined) {
            setEnrollment(candidate.enrollment ?? null);
            setEnrollmentName(candidate.enrollment?.sequence_name ?? null);
            setEnrollmentLoading(false);
            return;
        }

        const orgId = localStorage.getItem("current_org_id");
        let cancelled = false;
        setEnrollment(null);
        setEnrollmentName(null);
        setEnrollmentLoading(true);
        axios.get(`${process.env.REACT_APP_SOURCING_API_URL}/email/enrollments`, {
            params: { candidate_id: candidate.candidate_id, org_id: orgId },
        })
            .then(async ({ data }) => {
                if (cancelled || !data?.enrolled) return;
                setEnrollment(data.enrollment);
                const { data: seqs } = await axios.get(
                    `${process.env.REACT_APP_SOURCING_API_URL}/email/sequences`,
                    { params: { org_id: orgId } }
                );
                if (cancelled) return;
                setEnrollmentName((seqs || []).find(s => s.id === data.enrollment.sequence_id)?.name || null);
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setEnrollmentLoading(false); });
        return () => { cancelled = true; };
    }, [candidate?.candidate_id, candidate?.enrollment]);

    // Pipeline status is now a standalone outreach stage — independent of
    // shortlisting/saving, which have their own dedicated actions.
    const applyStatus = async (newStatus) => {
        const recruiterId = localStorage.getItem("org_admin_id");
        const orgId = localStorage.getItem("current_org_id");
        // Status is a statement about a role — without a job there's nothing to attach it to.
        if (!jobId) return;

        setPipelineStatus(newStatus);
        setStatusDropdownOpen(false);
        axios.post(`${process.env.REACT_APP_SOURCING_API_URL}/sourcing/pipeline`, {
            candidate_id: candidate.candidate_id,
            recruiter_id: recruiterId,
            organisation_id: orgId,
            action: newStatus,
            job_id: jobId,
        }).catch(() => {});
        axios.post(`${process.env.REACT_APP_SOURCING_API_URL}/sourcing/activity`, {
            action: "status_changed",
            candidate_id: candidate.candidate_id,
            recruiter_id: recruiterId,
            organisation_id: orgId,
            metadata: {
                recruiter_name: recruiterName,
                candidate_name: candidate.full_name,
                new_status: newStatus,
                new_status_label: STATUS_OPTIONS.find(o => o.value === newStatus)?.label,
                job_id: jobId,
            },
        }).catch(() => {});
    };

    const handleShortlist = async () => {
        if (shortlisting) return;
        setShortlisting(true);
        await onShortlist(candidate);
        setShortlisting(false);
    };

    // The panel stays mounted while the recruiter switches candidates, so reset
    // the email/phone display whenever a different candidate is shown.
    useEffect(() => {
        // Seed from the candidate object (which the parent list keeps updated, see
        // the onContactUpdate write-backs below) so an already-known email/phone shows
        // immediately when the panel is reopened — instead of blanking and requiring a
        // reveal click or a page refresh.
        setEmail(candidate?.email || null);
        setEmailType(candidate?.email_type || null);
        setRevealing(false);
        setManualEntry(false);
        setManualInput("");
        setRevealError(null);
        setPhone(candidate?.phone || null);
        setCheckingPhone(false);
        setPhoneManualEntry(false);
        setPhoneInput("");
        setPhoneError(null);
    }, [candidate?.candidate_id]);

    // Hand a revealed/saved contact value back to whoever owns the list so the row
    // behind the panel learns it too. It must go through the owner rather than being
    // written onto `candidate` directly: on the Find Candidates screen that object is a
    // Redux row, which Immer has frozen — assigning to it throws, and the throw used to
    // land in the catch below and report a save that had in fact succeeded.
    const publishContact = (changes) => {
        if (!candidate?.candidate_id) return;
        onContactUpdate?.(candidate.candidate_id, changes);
    };

    // DB-only lookup — never calls CrustData. Used when "Email/Phone available"
    // is clicked, to surface a value someone already revealed/saved earlier.
    const checkContact = async () => {
        const { data } = await axios.get(
            `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/candidate/${candidate.candidate_id}/contact`
        );
        return data;
    };

    const handleCheckEmail = async () => {
        setRevealing(true);
        setRevealError(null);
        try {
            const data = await checkContact();
            if (data.email) {
                setEmail(data.email);
                setEmailType(data.email_type);
                publishContact({ email: data.email, email_type: data.email_type });
            } else {
                setRevealError("No email found on file.");
            }
        } catch {
            setRevealError("Failed to check email. Try again.");
        } finally {
            setRevealing(false);
        }
    };

    const handleCheckPhone = async () => {
        setCheckingPhone(true);
        setPhoneError(null);
        try {
            const data = await checkContact();
            if (data.phone) {
                setPhone(data.phone);
                publishContact({ phone: data.phone, has_phone_number: true });
            } else {
                setPhoneError("No phone number found on file.");
            }
        } catch {
            setPhoneError("Failed to check phone. Try again.");
        } finally {
            setCheckingPhone(false);
        }
    };

    const handleSavePhone = async () => {
        if (!phoneInput) return;
        if (!isValidPhone(phoneInput)) {
            setPhoneError(INVALID_PHONE_MESSAGE);
            return;
        }
        setSavingPhone(true);
        setPhoneError(null);
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/add-phone`,
                { candidate_id: candidate.candidate_id, phone: phoneInput }
            );
            setPhone(data.phone);
            // Write back onto the row the parent list holds, so the number still shows
            // when this profile is closed and reopened — and so the enroll gate, which
            // reads the row, sees it without a page refresh.
            publishContact({ phone: data.phone, has_phone_number: true });
            setPhoneManualEntry(false);
            setPhoneInput("");
        } catch {
            setPhoneError("Failed to save phone. Try again.");
        } finally {
            setSavingPhone(false);
        }
    };

    const handleSaveManual = async () => {
        if (!manualInput.trim()) return;
        if (!isValidEmail(manualInput)) {
            setRevealError(INVALID_EMAIL_MESSAGE);
            return;
        }
        setSavingManual(true);
        setRevealError(null);
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/add-email`,
                { candidate_id: candidate.candidate_id, email: manualInput.trim() }
            );
            setEmail(data.email);
            setEmailType(data.email_type);
            // Write back onto the row the parent list holds, so the address still shows
            // when this profile is closed and reopened — and so the enroll gate, which
            // reads the row, sees it without a page refresh.
            publishContact({
                email: data.email,
                email_type: data.email_type,
                ...(data.email_type === "business"
                    ? { has_business_email: true }
                    : { has_personal_email: true }),
            });
            setManualEntry(false);
            setManualInput("");
        } catch {
            setRevealError("Failed to save email. Try again.");
        } finally {
            setSavingManual(false);
        }
    };

    const buildSummary = () => {
        const lines = [candidate.full_name || "Candidate"];
        if (candidate.current_title || candidate.current_company) {
            const exp = candidate.years_of_experience ? ` · ${candidate.years_of_experience}+ yrs experience` : "";
            lines.push(`• ${candidate.current_title || "—"}${candidate.current_company ? ` at ${candidate.current_company}` : ""}${exp}`);
        }
        (candidate.past_jobs || []).slice(0, 2).forEach(j => {
            lines.push(`• Previously ${j.title || ""}${j.name ? ` at ${j.name}` : ""}`);
        });
        const edu = (candidate.education || [])[0];
        if (edu) lines.push(`• ${[edu.degree, edu.field_of_study].filter(Boolean).join(", ")}${edu.school ? ` — ${edu.school}` : ""}`);
        if ((candidate.skills || []).length) lines.push(`• Skilled in ${candidate.skills.slice(0, 8).join(", ")}`);
        if (candidate.location) lines.push(`• Based in ${candidate.location}`);
        return lines.join("\n");
    };

    const exportPDF = () => {
        const doc = new jsPDF({ unit: "pt", format: "a4" });
        const margin = 48;
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();
        const maxW = pageW - margin * 2;
        let y = margin;
        const write = (text, { size = 11, bold = false, gap = 8, color = [40, 40, 40] } = {}) => {
            if (!text) return;
            doc.setFont("helvetica", bold ? "bold" : "normal");
            doc.setFontSize(size);
            doc.setTextColor(color[0], color[1], color[2]);
            doc.splitTextToSize(String(text), maxW).forEach(line => {
                if (y > pageH - margin) { doc.addPage(); y = margin; }
                doc.text(line, margin, y);
                y += size + 3;
            });
            y += gap;
        };
        write(candidate.full_name || "Candidate", { size: 20, bold: true, gap: 4 });
        const sub = [candidate.current_title, candidate.current_company].filter(Boolean).join(" at ");
        write(sub, { size: 12, color: [90, 90, 90], gap: 2 });
        write(candidate.location, { size: 10, color: [130, 130, 130] });
        if (candidate.email) write(`Email: ${candidate.email}`, { size: 10, color: [90, 90, 90], gap: 2 });
        if (candidate.linkedin_url) write(`LinkedIn: ${candidate.linkedin_url}`, { size: 10, color: [90, 90, 90] });
        if (candidate.summary) { write("Summary", { size: 13, bold: true, gap: 4 }); write(candidate.summary); }
        const jobs = [...(candidate.current_jobs || []), ...(candidate.past_jobs || [])];
        if (jobs.length) {
            write("Experience", { size: 13, bold: true, gap: 4 });
            jobs.forEach(j => {
                write(`${j.title || ""}${j.name ? ` — ${j.name}` : ""}`, { size: 11, bold: true, gap: 1 });
                const dates = [j.start_date, j.end_date || (j.is_current ? "Present" : "")].filter(Boolean).join(" - ");
                write(dates, { size: 9, color: [130, 130, 130] });
            });
        }
        if ((candidate.education || []).length) {
            write("Education", { size: 13, bold: true, gap: 4 });
            candidate.education.forEach(e => write(`${e.school || ""}${(e.degree || e.field_of_study) ? ` — ${[e.degree, e.field_of_study].filter(Boolean).join(", ")}` : ""}`, { size: 11, gap: 4 }));
        }
        if ((candidate.skills || []).length) { write("Skills", { size: 13, bold: true, gap: 4 }); write(candidate.skills.join(", ")); }
        doc.save(`${(candidate.full_name || "profile").replace(/\s+/g, "_")}.pdf`);
    };

    const summaryText = buildSummary();
    const emailAvailable = !!(candidate.has_business_email || candidate.has_personal_email);
    const phoneAvailable = !!candidate.has_phone_number;

    return (
        <>
            <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
            <div className={`fixed top-0 right-0 h-full bg-white dark:bg-[#1B1524] z-50 shadow-2xl flex ${showDetail ? "w-[92vw] max-w-[1200px]" : "w-[560px]"}`}>

              {/* LEFT column — same fixed width whether expanded or not */}
              <div className="w-[560px] shrink-0 h-full overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">

                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700/50 sticky top-0 bg-white dark:bg-[#1B1524] z-10">
                    <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setShowDetail(v => !v)}
                            title={showDetail ? "Collapse" : "Expand"}
                            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 p-1"
                        >
                            {showDetail ? (
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 5v14" />
                                    <path d="M4 12h11" />
                                    <path d="M11 8l4 4-4 4" />
                                </svg>
                            ) : (
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 5v14" />
                                    <path d="M20 12H9" />
                                    <path d="M13 8l-4 4 4 4" />
                                </svg>
                            )}
                        </button>

                        {/* More options */}
                        <div className="relative" ref={moreRef}>
                            <button
                                onClick={() => setMoreOpen(o => !o)}
                                title="More"
                                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 p-1"
                            >
                                <MoreHorizontal className="w-5 h-5" />
                            </button>
                            {moreOpen && (
                                <div className="absolute right-0 mt-1 w-60 bg-white dark:bg-[#1B1524] border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg z-30 py-1 overflow-hidden">
                                    {canSchedule && (
                                        <button
                                            onClick={() => { setMoreOpen(false); startSchedule(candidate); }}
                                            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 transition-all text-left"
                                        >
                                            <CalendarClock className="w-4 h-4 text-gray-400 dark:text-gray-500" /> Schedule Interview
                                        </button>
                                    )}
                                    {onFindSimilar && (
                                        <button
                                            onClick={() => { setMoreOpen(false); onFindSimilar(candidate); }}
                                            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 transition-all text-left"
                                        >
                                            <Users className="w-4 h-4 text-gray-400 dark:text-gray-500" /> Find Similar Profiles
                                        </button>
                                    )}
                                    <button
                                        onClick={() => { setMoreOpen(false); setSummaryOpen(true); }}
                                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 transition-all text-left"
                                    >
                                        <FileText className="w-4 h-4 text-gray-400 dark:text-gray-500" /> Summarize Full Profile
                                    </button>
                                    <button
                                        onClick={() => { setMoreOpen(false); exportPDF(); }}
                                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 transition-all text-left"
                                    >
                                        <Download className="w-4 h-4 text-gray-400 dark:text-gray-500" /> Export Profile (PDF)
                                    </button>
                                </div>
                            )}
                        </div>
                        {candidate.linkedin_url && (
                            <a href={candidate.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-300">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                </svg>
                            </a>
                        )}
                    </div>
                </div>

                <div className="px-6 py-6">
                    <div className="flex items-start gap-4">
                        <CandidateAvatar candidate={candidate} size={56} />
                        <div className="min-w-0">
                            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">{candidate.full_name}</h2>
                            {candidate.current_title && (
                                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 truncate">
                                    {candidate.current_title}
                                    {candidate.current_company && <span> at {candidate.current_company}</span>}
                                </p>
                            )}
                            {candidate.location && (
                                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">{candidate.location}</p>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-3 mt-5">
                        {candidate.current_company && (
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-gray-700/50">
                                <div className="w-4 h-4 rounded bg-gray-200 dark:bg-white/10" />
                                <span className="text-xs text-gray-600 dark:text-gray-300 truncate max-w-[140px]">
                                    {candidate.current_company}
                                </span>
                            </div>
                        )}
                        {candidate.education?.[0]?.school && (
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-gray-700/50">
                                <div className="w-4 h-4 rounded bg-gray-200 dark:bg-white/10" />
                                <span className="text-xs text-gray-600 dark:text-gray-300 truncate max-w-[140px]">
                                    {candidate.education[0].school}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="flex gap-3 mt-6">
                        {/* One control, one look: the button keeps its border-btn styling in both
                            states and only the label changes, so "Shortlisted" reads as something
                            you can click again to undo rather than as a status badge. !py-2.5
                            matches the sibling Enroll button's height (border-btn's own padding
                            is 8px). Re-entrancy is handled by handleShortlist's own guard, so no
                            disabled attribute — border-btn's :disabled rule paints a dark border
                            over the gradient. .border-btn only has a .light rule, so
                            border-btn-dark draws the same outline in dark theme. */}
                        <button
                            onClick={handleShortlist}
                            aria-busy={shortlisting}
                            title={shortlisted ? "Remove from shortlist" : "Add to shortlist"}
                            className="border-btn border-btn-dark flex-1 flex items-center justify-center gap-2 !py-2.5 font-medium transition-all"
                        >
                            {shortlisting
                                ? (shortlisted ? "Removing…" : "Shortlisting…")
                                : (shortlisted ? "Shortlisted" : "Shortlist")}
                        </button>
                        {enrollmentLoading ? (
                            <div className="flex-1 px-4 py-2.5 rounded-full bg-gray-100 dark:bg-white/10 animate-pulse" aria-hidden="true">
                                <span className="block h-5" />
                            </div>
                        ) : (
                            <div className="flex-1">
                                {/* Greyed but still clickable. The flag comes off the list row, so it
                                    can be stale if the enrollment was cancelled elsewhere since the
                                    page loaded; hard-disabling would strand the recruiter with no way
                                    to enroll. The modal re-checks and reports the truth either way, so
                                    a stale flag costs a click instead of a dead end. */}
                                <button
                                    onClick={() => onEnroll?.(candidate)}
                                    title={enrollment
                                        ? `Already enrolled in ${enrollmentName || "an outreach"}`
                                        : undefined}
                                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                                        enrollment
                                            ? "bg-gray-100 dark:bg-white/10 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-white/15"
                                            : "text-white"
                                    }`}
                                    style={enrollment ? undefined : { background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                                >
                                    {enrollment ? "Already in Outreach" : "Enroll to Outreach"}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Find Similar / Summarize / Export moved to the header "…" menu.
                        Activity lives in the expanded detail view. */}

                    {/* Overview Tab */}
                    {activeTab === "overview" && <>

                    <div className="border-t border-gray-100 dark:border-gray-700/50 my-6" />

                    <div className="flex items-center justify-between py-3.5 border-b border-gray-50 dark:border-gray-700/50">
                        <span className="text-sm text-gray-400 dark:text-gray-500 flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                            </svg>
                            Status
                        </span>
                        <div className="relative" ref={statusDropdownRef}>
                            <button
                                onClick={() => jobId && setStatusDropdownOpen(o => !o)}
                                title={jobId ? undefined : "Open this candidate from a job role to set status — status is tracked per role"}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-white/5 transition-all text-sm text-gray-700 dark:text-gray-300 ${
                                    jobId ? "hover:border-gray-300 dark:hover:border-gray-500" : "cursor-default opacity-70"
                                }`}
                            >
                                <span
                                    className="w-2 h-2 rounded-full shrink-0"
                                    style={{ backgroundColor: STATUS_OPTIONS.find(o => o.value === pipelineStatus)?.color ?? "#d1d5db" }}
                                />
                                {STATUS_OPTIONS.find(o => o.value === pipelineStatus)?.label ?? "—"}
                                <svg className="w-3 h-3 text-gray-400 dark:text-gray-500 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {statusDropdownOpen && (
                                <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-[#1B1524] border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg z-20 py-1 overflow-hidden">
                                    {STATUS_OPTIONS.map(opt => (
                                        <button
                                            key={opt.value}
                                            onClick={() => applyStatus(opt.value)}
                                            className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-all text-left ${pipelineStatus === opt.value ? "bg-gray-50 dark:bg-white/5 font-medium dark:text-white" : "text-gray-700 dark:text-gray-300"}`}
                                        >
                                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: opt.color }} />
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Seen before — engagement on *other* roles. Deliberately not merged
                        into the status above: knowing you already emailed this person for
                        another job is what stops a second approach in the same week, but it
                        says nothing about where they stand on this one. */}
                    {/* Unlike status and enrollment, this is a cross-job aggregate — too
                        expensive to put on every row of a list to serve the few panels
                        actually opened, so it stays a fetch. It's always mounted and
                        collapsed to zero height instead of being conditionally rendered:
                        arriving history then expands the row smoothly rather than popping
                        in, and the common case (no other roles) never moves at all. */}
                    <div className={`grid transition-all duration-300 ease-out ${
                        (otherJobs.pipeline.length > 0 || otherJobs.sequences.length > 0)
                            ? "grid-rows-[1fr] opacity-100"
                            : "grid-rows-[0fr] opacity-0"
                    }`}>
                      <div className="overflow-hidden">
                        <div className="py-3.5 border-b border-gray-50 dark:border-gray-700/50">
                            <div className="flex items-center gap-2 mb-2.5">
                                <span className="text-sm text-gray-400 dark:text-gray-500 flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Seen before
                                </span>
                                <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-300 font-medium">
                                    Other roles
                                </span>
                            </div>

                            <div className="flex flex-col gap-1.5">
                                {otherJobs.pipeline.map(row => (
                                    <div
                                        key={`p-${row.job_id}`}
                                        className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-gray-700/50"
                                    >
                                        <span className="text-xs text-gray-600 dark:text-gray-300 truncate min-w-0">
                                            {row.job_role}
                                        </span>
                                        <span className="flex items-center gap-1.5 shrink-0">
                                            <span
                                                className="w-2 h-2 rounded-full"
                                                style={{ backgroundColor: STATUS_OPTIONS.find(o => o.value === row.status)?.color ?? "#d1d5db" }}
                                            />
                                            <span className="text-xs text-gray-500 dark:text-gray-400">{row.status_label}</span>
                                        </span>
                                    </div>
                                ))}

                                {otherJobs.sequences.map(row => (
                                    <div
                                        key={`s-${row.enrollment_id}`}
                                        className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-gray-700/50"
                                    >
                                        <span className="text-xs text-gray-600 dark:text-gray-300 truncate min-w-0">
                                            {row.sequence_name || "Outreach"}
                                            {row.job_role ? ` · ${row.job_role}` : ""}
                                        </span>
                                        <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${
                                            row.status === "active"
                                                ? "bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-300"
                                                : "bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400"
                                        }`}>
                                            {row.status === "active" ? "In outreach" : row.status}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 py-3.5 border-b border-gray-50 dark:border-gray-700/50">
                        <span className="text-sm text-gray-400 dark:text-gray-500 flex items-center gap-2 shrink-0">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Email
                        </span>
                        {/* The editor replaces the address rather than sitting beside it — the panel is a
                            fixed 560px column and can't fit an existing email and the input on one line. */}
                        <div className="flex items-center gap-3 min-w-0 flex-1 justify-end">
                            {manualEntry ? (
                                /* The editor is what reports its own outcome: it stays open only
                                   while the save is unresolved, so a failure has to be visible
                                   here rather than in the read-only row it hasn't returned to. */
                                <div className="flex flex-col items-end gap-1 min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 min-w-0 w-full justify-end">
                                        <input
                                            type="email"
                                            // This holds the candidate's email, not the recruiter's —
                                            // browser contact autofill is never right here, and picking
                                            // a suggestion cross-fills other fields (the list search box).
                                            autoComplete="off"
                                            value={manualInput}
                                            onChange={(e) => { setManualInput(e.target.value); setRevealError(null); }}
                                            onKeyDown={(e) => e.key === "Enter" && handleSaveManual()}
                                            placeholder="email@example.com"
                                            className="text-sm bg-transparent dark:text-gray-200 border border-gray-200 dark:border-gray-600 rounded px-2 py-0.5 min-w-0 flex-1 focus:outline-none focus:border-purple-400"
                                            autoFocus
                                        />
                                        <button
                                            onClick={handleSaveManual}
                                            disabled={savingManual || !manualInput.trim()}
                                            className="text-xs text-white font-medium px-2.5 py-1 rounded hover:opacity-90 disabled:opacity-50 shrink-0"
                                            style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                                        >
                                            {savingManual ? "Saving..." : "Save"}
                                        </button>
                                        <button
                                            onClick={() => { setManualEntry(false); setManualInput(""); setRevealError(null); }}
                                            className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 shrink-0"
                                        >
                                            ×
                                        </button>
                                    </div>
                                    {revealError && <span className="text-xs text-red-400">{revealError}</span>}
                                </div>
                            ) : (
                                <>
                                    {email ? (
                                        <>
                                            <a href={`mailto:${email}`} className="text-sm text-green-600 dark:text-green-400 font-medium hover:underline truncate min-w-0">
                                                {email}
                                            </a>
                                            {emailType && (
                                                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium shrink-0 ${
                                                    emailType === "business"
                                                        ? "bg-blue-50 dark:bg-blue-500/15 text-blue-500 dark:text-blue-300"
                                                        : emailType === "personal"
                                                        ? "bg-purple-50 dark:bg-purple-500/15 text-purple-500 dark:text-purple-300"
                                                        : "bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400"
                                                }`}>
                                                    {emailType === "business" ? "Work" : emailType === "personal" ? "Personal" : "Added"}
                                                </span>
                                            )}
                                        </>
                                    ) : revealError ? (
                                        <span className="text-xs text-red-400">{revealError}</span>
                                    ) : emailAvailable ? (
                                        <button
                                            onClick={handleCheckEmail}
                                            disabled={revealing}
                                            className="text-sm text-purple-600 dark:text-purple-300 font-medium hover:underline disabled:opacity-50 flex items-center gap-1 shrink-0"
                                        >
                                            {revealing ? (
                                                <>
                                                    <div className="w-3 h-3 border border-purple-400 border-t-transparent rounded-full animate-spin" />
                                                    Checking...
                                                </>
                                            ) : (
                                                "Reveal Email"
                                            )}
                                        </button>
                                    ) : (
                                        <span className="text-sm text-gray-400 dark:text-gray-500">No email available</span>
                                    )}
                                    <span className="text-sm text-gray-300 dark:text-gray-600 shrink-0">|</span>
                                    <button
                                        onClick={() => { setManualInput(email || ""); setRevealError(null); setManualEntry(true); }}
                                        className="text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 shrink-0"
                                    >
                                        {email ? "Edit" : "Add Email"}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 py-3.5 border-b border-gray-50 dark:border-gray-700/50">
                        <span className="text-sm text-gray-400 dark:text-gray-500 flex items-center gap-2 shrink-0">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            Phone
                        </span>
                        <div className="flex items-center gap-3 min-w-0 flex-1 justify-end">
                            {phoneManualEntry ? (
                                <div className="flex flex-col items-end gap-1 min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 min-w-0 w-full justify-end">
                                        <PhoneNumberInput
                                            autoComplete="off"
                                            value={phoneInput}
                                            onChange={(value) => { setPhoneInput(value); setPhoneError(null); }}
                                            onKeyDown={(e) => e.key === "Enter" && handleSavePhone()}
                                            placeholder="+91 98765 43210"
                                            className="text-sm text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 rounded px-2 py-0.5 min-w-0 flex-1 focus-within:border-purple-400"
                                            autoFocus
                                        />
                                        <button
                                            onClick={handleSavePhone}
                                            disabled={savingPhone || !phoneInput}
                                            className="text-xs text-white font-medium px-2.5 py-1 rounded hover:opacity-90 disabled:opacity-50 shrink-0"
                                            style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                                        >
                                            {savingPhone ? "Saving..." : "Save"}
                                        </button>
                                        <button
                                            onClick={() => { setPhoneManualEntry(false); setPhoneInput(""); setPhoneError(null); }}
                                            className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 shrink-0"
                                        >
                                            ×
                                        </button>
                                    </div>
                                    {phoneError && <span className="text-xs text-red-400">{phoneError}</span>}
                                </div>
                            ) : (
                                <>
                                    {phone ? (
                                        <a href={`tel:${phone}`} className="text-sm text-green-600 dark:text-green-400 font-medium hover:underline truncate min-w-0">
                                            {phone}
                                        </a>
                                    ) : phoneError ? (
                                        <span className="text-xs text-red-400">{phoneError}</span>
                                    ) : phoneAvailable ? (
                                        <button
                                            onClick={handleCheckPhone}
                                            disabled={checkingPhone}
                                            className="text-sm text-purple-600 dark:text-purple-300 font-medium hover:underline disabled:opacity-50 flex items-center gap-1 shrink-0"
                                        >
                                            {checkingPhone ? (
                                                <>
                                                    <div className="w-3 h-3 border border-purple-400 border-t-transparent rounded-full animate-spin" />
                                                    Checking...
                                                </>
                                            ) : (
                                                "Reveal Phone"
                                            )}
                                        </button>
                                    ) : (
                                        <span className="text-sm text-gray-400 dark:text-gray-500">No phone number available</span>
                                    )}
                                    <span className="text-sm text-gray-300 dark:text-gray-600 shrink-0">|</span>
                                    <button
                                        onClick={() => { setPhoneInput(toE164(phone)); setPhoneError(null); setPhoneManualEntry(true); }}
                                        className="text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 shrink-0"
                                    >
                                        {phone ? "Edit" : "Add Phone"}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="border-t border-gray-100 dark:border-gray-700/50 my-6" />

                    {(candidate.current_jobs?.length > 0 || candidate.past_jobs?.length > 0) && (
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <h3 className="text-base font-semibold text-gray-800 dark:text-white">Experience</h3>
                                {candidate.years_of_experience > 0 && (
                                    <span className="text-xs text-gray-400 dark:text-gray-500">· {candidate.years_of_experience} years total</span>
                                )}
                            </div>
                            {candidate.current_jobs?.map((job, i) => (
                                <div key={i} className="flex gap-4 mb-5">
                                    <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-gray-600 shrink-0 flex items-center justify-center text-xs text-gray-400 dark:text-gray-500 font-medium">
                                        {job.name?.charAt(0) || "?"}
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-gray-800 dark:text-white">{job.title}</p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">{job.name}</p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                            {job.start_date
                                                ? new Date(job.start_date).toLocaleDateString("en-US", { month: "short", year: "numeric" })
                                                : ""
                                            }
                                            {" - Present"}
                                            {job.years_at_company && ` · ${job.years_at_company}`}
                                        </p>
                                        {job.employment_type && (
                                            <span className="inline-block mt-1 text-xs text-gray-400 dark:text-gray-500">{job.employment_type}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {candidate.past_jobs?.map((job, i) => (
                                <div key={i} className="flex gap-4 mb-5 opacity-75">
                                    <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-gray-600 shrink-0 flex items-center justify-center text-xs text-gray-400 dark:text-gray-500 font-medium">
                                        {job.name?.charAt(0) || "?"}
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{job.title}</p>
                                        <p className="text-sm text-gray-400 dark:text-gray-500">{job.name}</p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                            {job.start_date
                                                ? new Date(job.start_date).toLocaleDateString("en-US", { month: "short", year: "numeric" })
                                                : ""
                                            }
                                            {" - "}
                                            {job.end_date
                                                ? new Date(job.end_date).toLocaleDateString("en-US", { month: "short", year: "numeric" })
                                                : "Present"
                                            }
                                            {job.years_at_company && ` · ${job.years_at_company}`}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {candidate.education?.length > 0 && (
                        <>
                            <div className="border-t border-gray-100 dark:border-gray-700/50 my-6" />
                            <div>
                                <h3 className="text-base font-semibold text-gray-800 dark:text-white mb-4">Education</h3>
                                {candidate.education.map((edu, i) => (
                                    <div key={i} className="flex gap-4 mb-4">
                                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-gray-600 shrink-0 flex items-center justify-center text-xs text-gray-400 dark:text-gray-500 font-medium">
                                            {edu.school?.charAt(0) || "?"}
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-semibold text-gray-800 dark:text-white">{edu.school}</p>
                                            {edu.degree && (
                                                <p className="text-sm text-gray-400 dark:text-gray-500">
                                                    {edu.degree}
                                                    {edu.field_of_study && `, ${edu.field_of_study}`}
                                                </p>
                                            )}
                                            {(edu.start_year || edu.end_year) && (
                                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                                    {edu.start_year} - {edu.end_year}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {candidate.skills?.length > 0 && (
                        <>
                            <div className="border-t border-gray-100 dark:border-gray-700/50 my-6" />
                            <div>
                                <h3 className="text-base font-semibold text-gray-800 dark:text-white mb-4">Skills</h3>
                                <div className="flex flex-wrap gap-2">
                                    {candidate.skills.map((skill, i) => (
                                        <span key={i} className="px-3 py-1 rounded-full bg-purple-50 dark:bg-purple-500/15 text-purple-600 dark:text-purple-300 text-xs font-medium">
                                            {skill}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                    </>}
                </div>
              </div>

              {/* RIGHT column — Activity / Sequences tabs (only when expanded) */}
              {showDetail && (
                <div className="flex-1 min-w-0 h-full border-l border-gray-100 dark:border-gray-700/50">
                  <CandidateDetailTabs candidate={candidate} />
                </div>
              )}
            </div>

            {/* Profile Summary modal */}
            {summaryOpen && (
                <>
                    <div className="fixed inset-0 bg-black/40 z-[95]" onClick={() => setSummaryOpen(false)} />
                    <div className="fixed inset-0 z-[96] flex items-center justify-center p-4 pointer-events-none">
                        <div className="bg-white dark:bg-[#1B1524] rounded-2xl shadow-2xl w-full max-w-lg pointer-events-auto" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700/50">
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Profile Summary</h3>
                                <button onClick={() => setSummaryOpen(false)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="px-6 py-5">
                                <div className="border border-gray-200 dark:border-gray-600 rounded-xl p-4 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line max-h-[55vh] overflow-y-auto">
                                    {summaryText}
                                </div>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Generated from profile data. Confirm key information.</p>
                            </div>
                            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700/50 flex justify-end gap-2">
                                <button
                                    onClick={() => { navigator.clipboard?.writeText(summaryText); }}
                                    className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 flex items-center gap-2"
                                >
                                    <Copy className="w-4 h-4" /> Copy to Clipboard
                                </button>
                                <button
                                    onClick={() => setSummaryOpen(false)}
                                    className="px-5 py-2 rounded-lg text-sm font-medium text-white"
                                    style={{ background: "#6d28d9" }}
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {scheduleUI}
        </>
    );
};
